import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../Layout';
import '../../styles/BlogPost.css';
import { createRoot } from 'react-dom/client';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TableOfContents from './TableOfContents';
import { fetchPostContent, processMarkdownContent, fetchBlogPosts } from '../../services/blogService';

import BlogCard from './BlogCard';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toc, setToc] = useState([]);
  const BUCKET_URL = process.env.REACT_APP_BUCKET_URL;

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const { markdown, postInfo, release } = await fetchPostContent(BUCKET_URL, slug);
        const { content, tocItems } = processMarkdownContent(markdown, BUCKET_URL, release);

        setToc(tocItems);
        setPost({
          content,
          metadata: {
            ...postInfo,
            image_url: postInfo.image ? `${BUCKET_URL}/releases/${release}${postInfo.image}` : null
          }
        });

        // Update SEO Metadata
        document.title = `${postInfo.title} - Marcus Webb`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', postInfo.excerpt);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'description';
          meta.content = postInfo.excerpt;
          document.head.appendChild(meta);
        }

        // Fetch related posts
        const allPosts = await fetchBlogPosts(BUCKET_URL);
        const currentTags = postInfo.tags || [];
        const related = allPosts
          .filter(p => p.filename !== `${slug}.md`)
          .map(p => {
            const pTags = p.tags || [];
            const commonTags = pTags.filter(tag => currentTags.includes(tag));
            return { ...p, similarity: commonTags.length };
          })
          .filter(p => p.similarity > 0)
          .sort((a, b) => b.similarity - a.similarity)
          .slice(0, 2);
        
        setRelatedPosts(related);

      } catch (error) {
        console.error('Failed to load post:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug, BUCKET_URL]);

  useEffect(() => {
    if (post) {
      const codeBlocks = document.querySelectorAll('.markdown-content pre code');
      codeBlocks.forEach(block => {
        if (block.dataset.highlighted) return;
        block.dataset.highlighted = 'true';
        
        const langMatch = block.className.match(/language-(\w+)/);
        const lang = langMatch ? langMatch[1] : 'text';
        const code = block.textContent.replace(/\n$/, '');
        
        const pre = block.parentElement;
        const container = document.createElement('div');
        pre.replaceWith(container);
        
        const root = createRoot(container);
        
        const CodeContainer = () => {
          const [copied, setCopied] = useState(false);
          const handleCopy = () => {
            navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          };
          
          return (
            <div style={{ position: 'relative' }}>
              <button 
                className="copy-button" 
                onClick={handleCopy}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <SyntaxHighlighter 
                style={vscDarkPlus} 
                language={lang}
                customStyle={{ margin: '2rem 0', borderRadius: '12px', fontSize: '0.9rem' }}
              >
                {code}
              </SyntaxHighlighter>
            </div>
          );
        };
        
        root.render(<CodeContainer />);
      });
    }
  }, [post]);

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) return <Layout><div>Loading...</div></Layout>;
  if (!post) return <Layout><div>Post not found</div></Layout>;

  return (
    <Layout>
      <div className="blog-post-container">
        <TableOfContents toc={toc} onHeadingClick={scrollToHeading} />
        <article className="blog-post">
          {post.metadata.image_url && (
            <div className="blog-post-image">
              <img src={post.metadata.image_url} alt={post.metadata.title} />
            </div>
          )}
          <h1>{post.metadata.title}</h1>
          <div className="blog-post-meta">
            <time>{post.metadata.date}</time>
            {post.metadata.readingTime && <span className="reading-time">{post.metadata.readingTime}</span>}
          </div>
          {post.metadata.tags && (
            <div className="blog-post-tags">
              {post.metadata.tags.map(tag => (
                <span key={tag} className="post-tag">{tag}</span>
              ))}
            </div>
          )}
          <div 
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
          
          {relatedPosts.length > 0 && (
            <div className="related-posts-section">
              <h2>Related Posts</h2>
              <div className="related-posts-grid">
                {relatedPosts.map(rp => (
                  <BlogCard key={rp.filename} post={rp} />
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </Layout>
  );
}